<script setup lang="ts">
import {
  QImg,
  type QImgProps,
} from 'quasar';
import {computed} from 'vue';
import {
  buildImageTransformUrl,
  calculateImageSizeFactor,
  type ImageTransformOptions,
} from '@/ts/utils/pure-functions';

const props = withDefaults(defineProps<{
  src: string; // this can be either a UUID (balm directus media) or a URL (rec-directus media)
  loading?: QImgProps['loading'];
  width?: number;
  height?: number;
  left?: number;
  top?: number;
  skipLoader?: boolean;
  // https://docs.directus.io/reference/files.html#custom-transformations
  format?: 'auto' | 'jpg' | 'png' | 'tiff' | 'webp';
  altTxt?: string;
}>(), {
  loading: 'lazy',
  format: 'webp',
});


const calculatedSrc = computed(() => {
  const sizeFactor = calculateImageSizeFactor(window.devicePixelRatio ?? 1);
  const options: ImageTransformOptions = {format: props.format};
  if (props.width) {
    options.width = props.width * sizeFactor;
  }
  if (props.height) {
    options.height = props.height * sizeFactor;
  }
  if (props.left !== undefined) { // eslint-disable-line no-undefined
    options.left = props.left;
  }
  if (props.top !== undefined) { // eslint-disable-line no-undefined
    options.top = props.top;
  }
  return buildImageTransformUrl(props.src, options);
});

const imageAlt = computed((): string | undefined =>
  props.altTxt || undefined); // eslint-disable-line no-undefined

const isDecorative = computed((): boolean => !props.altTxt);

// Calculate aspect ratio for q-img to preserve space during loading
const aspectRatio = computed((): number | undefined =>
  props.width && props.height ? props.width / props.height : undefined); // eslint-disable-line no-undefined

</script>
<template>
  <img v-if="skipLoader" :src="calculatedSrc" :alt="imageAlt" :aria-hidden="isDecorative || undefined"/>
  <q-img
    v-else
    :key="calculatedSrc"
    :src="calculatedSrc"
    :loading="loading"
    :alt="imageAlt"
    :aria-hidden="isDecorative || undefined"
    :ratio="aspectRatio"
    class="image"
    no-transition
  >
    <template #error>
      <div class="error"/>
    </template>
    <template #loading>
      <q-skeleton 
        class="loading"
        type="QChip"
      />
    </template>
  </q-img>
</template>

<style lang="scss" scoped>
.loading, .error {
  width: 100%;
  height: 100%;
}

.error {
  @include background-lines(var(--color-primary));
}
</style>
